/**
 * Escritas da página de Categorias pelos repositórios do app — as mesmas regras de
 * app/categories/[id].tsx: nome de subcategoria único dentro da categoria (sem diferenciar
 * maiúsculas), remover subcategoria desclassifica o que apontava pra ela, apagar categoria exige
 * outra ativa do mesmo tipo pra receber os lançamentos, mesclar leva tudo pro destino.
 */
import {
  archiveCategory, createCategory, createSubcategory, deleteCategory, deleteSubcategory, getDeleteFallbackCategoryName,
  getSubcategoriesByCategoryId, mergeCategories, reorderSubcategories, unarchiveCategory, updateCategory, updateSubcategory,
} from '@/db/repositories/categoryRepository';
import type { CategoriesEditor, SubcategoryDraft } from '../viewer/lib/editing';

/** Aplica a lista do diálogo: remove o que saiu, renomeia o que mudou, cria o que é novo e reordena se a ordem mudou. */
async function syncSubcategories(categoryId: string, drafts: SubcategoryDraft[]): Promise<void> {
  const current = await getSubcategoriesByCategoryId(categoryId);
  const wanted = drafts.map(d => ({ id: d.id, name: d.name.trim() })).filter(d => d.name);
  const keep = new Set(wanted.map(d => d.id).filter((id): id is string => !!id));
  // Primeiro o que sai: libera o nome pra quem for renomeado ou criado com ele.
  for (const s of current) if (!keep.has(s.id)) await deleteSubcategory(s.id);
  const ids: string[] = [];
  for (const d of wanted) {
    if (d.id) {
      const was = current.find(s => s.id === d.id);
      if (was && was.name !== d.name) await updateSubcategory(d.id, { name: d.name });
      ids.push(d.id);
    } else {
      ids.push((await createSubcategory({ categoryId, name: d.name })).id);
    }
  }
  const asIs = [...current.filter(s => keep.has(s.id)).map(s => s.id), ...ids.filter(id => !keep.has(id))];
  if (ids.length > 1 && ids.join() !== asIs.join()) await reorderSubcategories(ids);
}

export function createCategoriesEditor(after: () => Promise<void>): CategoriesEditor {
  const done = async <T,>(v: T): Promise<T> => { await after(); return v; };
  return {
    async create(input) {
      const c = await createCategory({ name: input.name.trim(), type: input.type, icon: input.icon, color: input.color });
      // A categoria já existe mesmo se uma subcategoria falhar (nome repetido): a tela recarrega de qualquer jeito.
      try { await syncSubcategories(c.id, input.subcategories); } finally { await after(); }
      return c.id;
    },
    async update(id, input) {
      await updateCategory(id, { name: input.name.trim(), icon: input.icon, color: input.color });
      try { await syncSubcategories(id, input.subcategories); } finally { await after(); }
    },
    async archive(id) { await archiveCategory(id); await after(); },
    async unarchive(id) { await unarchiveCategory(id); await after(); },
    async merge(sourceId, targetId) { return done(await mergeCategories(sourceId, targetId)); },
    async remove(id) { return done(await deleteCategory(id)); },
    fallbackName: (id) => getDeleteFallbackCategoryName(id),
    async createSubcategory(categoryId, name) { return done((await createSubcategory({ categoryId, name })).id); },
    async removeSubcategory(id) { await deleteSubcategory(id); await after(); },
  };
}
